import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BeatLoader } from "react-spinners";
import {
  applyMigration,
  createMigration,
  getMigration,
  isInFlight,
  isRetryablePollFailure,
  MigrationJobDto,
  MigrationOptionsType,
} from "../../api/migrationsApi";
import { getProjectTimezones } from "../../api/projectsApi";
import SelectInput from "../common/SelectInput";
import tailwindClassnames from "../common/TailwindClassnames";
import { errorDetail, uploadErrorMessage } from "../csvImport/useMigrationJob";
import MigrationReport from "./MigrationReport";

/**
 * Migrate from Wildlife Insights (AKL-747).
 *
 * Three steps, because a migration writes and there is no undo: upload a bundle, read the
 * report of what would happen, then confirm. Nothing touches the project until the
 * confirm.
 *
 * A migration is normally run **twice**, and the copy says so: the first run creates the
 * deployments (there are no images in akili yet, so the identifications have nothing to
 * attach to), and the second — after uploading with Akili Transfer — attaches them. Re-runs
 * are idempotent, so the second pass duplicates nothing.
 *
 * Opened from the project page (AKL-812): the New Project form runs it in create mode and
 * the Edit Project page in target-project mode; the organisation's Projects tab also opens
 * it in create mode. The starting organisation and time zone are read from props **at
 * mount**, so a caller that wants the modal to reflect values chosen since it last opened
 * should mount it only while it is open, as `ManageProjectDetails` does.
 */

/** How often to re-check a job the server is still working on. */
const POLL_INTERVAL_MS = 2000;

/**
 * Consecutive poll failures tolerated before asking the user.
 * A large bundle takes minutes to walk and will occasionally hit a dropped connection;
 * three strikes with backoff (2s, 4s, 8s) distinguishes a blip from an unreachable server.
 */
const MAX_POLL_FAILURES = 3;

/**
 * Elements the Tab-trap considers focusable.
 *
 * Duplicated from `ReportProblemModal.tsx` (itself duplicated from `ReviewPageFilter.tsx`):
 * neither exports it, and exporting would edit a shared component for one consumer. The
 * `tabIndex >= 0` filter applied alongside it keeps the panel itself, which carries
 * `tabIndex={-1}` so it can take focus on open, out of the Tab cycle.
 */
const FOCUSABLE_SELECTOR =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** An organisation the user may create a project in. */
export type MigrationOrgOption = {
  org_id: number;
  org_name?: string;
};

interface MigrateFromWildlifeInsightsProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The project to migrate into, or null to create the one the bundle describes.
   *
   * Passing a project is how the second pass attaches identifications to images that are
   * now in akili — the Edit Project page opens it this way. Passing null puts the wizard in
   * create mode, which is how the New Project form and the organisation's Projects tab
   * open it.
   */
  projectId: number | null;
  projectName?: string;
  /**
   * The organisation to start with. In target mode it is the project's organisation and is
   * fixed. In create mode it is a preselection — the one the New Project form already has
   * chosen — and the user may still pick another from `organizations`.
   */
  orgId?: number;
  /**
   * Organisations the user may create a project in. Required in create mode: a project has
   * to belong to one, and a user may belong to several. With exactly one entry it is
   * preselected, so the common case is not a pointless choice; with more, the picker is
   * shown, starting on `orgId` when that is given.
   */
  organizations?: MigrationOrgOption[];
  /**
   * The time zone to start with in create mode, when the caller already knows it (the New
   * Project form collects one). The user may still change it; blank means ask.
   */
  initialTimezone?: string;
  /** Called after a migration is applied, so the caller can refresh what it shows. */
  onApplied?: (job: MigrationJobDto) => void;
}

const MigrateFromWildlifeInsights: React.FC<
  MigrateFromWildlifeInsightsProps
> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  orgId,
  organizations,
  initialTimezone,
  onApplied,
}) => {
  const { t } = useTranslation();

  // Create mode is decided by the caller, not by a checkbox: opening this from the New
  // Project form or the projects list means creating, opening it from an existing project
  // means migrating into that project. An earlier version offered a checkbox that was
  // never reachable.
  const createProject = projectId === null;

  const [bundle, setBundle] = useState<File | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(
    orgId ?? (organizations?.length === 1 ? organizations[0].org_id : null),
  );
  const [timezone, setTimezone] = useState(initialTimezone ?? "");
  const [timezones, setTimezones] = useState<string[]>([]);
  const [timezonesLoading, setTimezonesLoading] = useState(false);
  const [timezonesError, setTimezonesError] = useState(false);
  // Bumped by "Try again" to re-run the time zone fetch after a failure.
  const [timezonesAttempt, setTimezonesAttempt] = useState(0);
  const [createMissingDeployments, setCreateMissingDeployments] =
    useState(true);
  const [markReviewed, setMarkReviewed] = useState(true);
  // AKL-754 round 2. On by default: a migration behaves as it did before the option
  // existed unless someone unticks it.
  const [migrateAiPredictions, setMigrateAiPredictions] = useState(true);

  const [job, setJob] = useState<MigrationJobDto | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pollFailure, setPollFailure] = useState<string | null>(null);

  // Kept in a ref as well as state so the polling effect can cancel cleanly without
  // becoming a dependency of itself.
  const cancelled = useRef(false);

  const reset = useCallback(() => {
    setBundle(null);
    setJob(null);
    setBusy(false);
    setError(null);
    setPollFailure(null);
    // The organisation-level caller keeps this panel mounted and toggles isOpen, so a
    // choice made on a cancelled attempt must not carry into the next one (#808 review).
    setCreateMissingDeployments(true);
    setMarkReviewed(true);
    setMigrateAiPredictions(true);
    setTimezone(initialTimezone ?? "");
    setSelectedOrgId(
      orgId ?? (organizations?.length === 1 ? organizations[0].org_id : null),
    );
  }, [orgId, organizations, initialTimezone]);

  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  useEffect(() => {
    cancelled.current = false;
    return () => {
      cancelled.current = true;
    };
  }, []);

  /**
   * Load the time zones the API accepts, once, and only when a project is being created.
   *
   * Fetched here rather than taken as a prop so a target-mode caller does not have to
   * request a list it never shows. From the API rather than the browser for the reason
   * `getProjectTimezones` documents: the server validates against its own tzdata, so a
   * browser with a different database could offer a zone that is then rejected.
   *
   * `timezonesLoading` drives the picker's loading state; it is cleared when the request
   * settles or the effect is torn down, so a modal closed mid-request does not reopen
   * stuck on "Loading...".
   *
   * A failure is shown in the modal with a "Try again" button (which bumps
   * `timezonesAttempt` to re-run this effect) rather than only logged: without the list a
   * user with no zone passed in cannot pick one, and the shared axios interceptor raises
   * no toast for a 4xx, so a console line would be the only trace (#826 review).
   */
  useEffect(() => {
    if (!isOpen || !createProject || timezones.length > 0) return;
    let stale = false;
    setTimezonesLoading(true);
    setTimezonesError(false);
    (async () => {
      try {
        const loaded = await getProjectTimezones();
        if (!stale && !cancelled.current) setTimezones(loaded);
      } catch (err) {
        // The picker keeps any zone the caller passed in; the message and "Try again"
        // below it are the way out when there is none.
        console.error("Could not load time zones:", err);
        if (!stale && !cancelled.current) setTimezonesError(true);
      } finally {
        if (!stale && !cancelled.current) setTimezonesLoading(false);
      }
    })();
    return () => {
      stale = true;
      setTimezonesLoading(false);
    };
  }, [isOpen, createProject, timezones.length, timezonesAttempt]);

  /**
   * Poll a job the server is still working on until it settles.
   *
   * Deliberately not a `setInterval`: overlapping requests on a slow connection would
   * queue up behind each other and the backoff below could not apply.
   */
  useEffect(() => {
    if (!isOpen || job === null || !isInFlight(job.status)) return;

    let failures = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let stopped = false;

    const tick = async () => {
      try {
        const next = await getMigration(job.guid);
        if (stopped || cancelled.current) return;
        failures = 0;
        setPollFailure(null);
        setJob(next);
        if (isInFlight(next.status)) {
          timer = setTimeout(tick, POLL_INTERVAL_MS);
        }
      } catch (err) {
        if (stopped || cancelled.current) return;
        failures += 1;
        if (!isRetryablePollFailure(err) || failures >= MAX_POLL_FAILURES) {
          setPollFailure(
            t(
              "akili lost track of this migration. It may still be running — reopen this window to check.",
            ),
          );
          return;
        }
        // Exponential backoff: 2s, 4s, 8s.
        timer = setTimeout(tick, POLL_INTERVAL_MS * 2 ** failures);
      }
    };

    timer = setTimeout(tick, POLL_INTERVAL_MS);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
    };
  }, [isOpen, job, t]);

  const options = (): MigrationOptionsType => ({
    // Non-null by the time this runs: `canUpload` requires an organisation.
    org_id: selectedOrgId as number,
    target_project_id: createProject ? null : projectId,
    create_project: createProject,
    timezone: createProject ? timezone.trim() : null,
    create_missing_deployments: createMissingDeployments,
    mark_reviewed: markReviewed,
    migrate_ai_predictions: migrateAiPredictions,
  });

  const handleUpload = async () => {
    if (!bundle) return;
    setBusy(true);
    setError(null);
    try {
      const created = await createMigration(bundle, options());
      // Guarded for the same reason the polling effect is: the upload of a large bundle
      // takes a while, and the user may close the panel or navigate away mid-flight.
      if (cancelled.current) return;
      setJob(created);
    } catch (err) {
      if (cancelled.current) return;
      // Keyed on the status, not on whether a detail came back: a 429 (another migration
      // still running) used to read as a broken file (AKL-1040).
      setError(
        uploadErrorMessage(err, {
          unreadable: t(
            "akili could not read that file as a Wildlife Insights export.",
          ),
          inProgress: t(
            "You already have a migration in progress. Wait for it to finish.",
          ),
          retry: t("akili could not check this file. Try again in a moment."),
        }),
      );
    } finally {
      if (!cancelled.current) setBusy(false);
    }
  };

  const handleApply = async () => {
    if (!job) return;
    setBusy(true);
    setError(null);
    try {
      const next = await applyMigration(job.guid);
      if (cancelled.current) return;
      setJob(next);
    } catch (err) {
      if (cancelled.current) return;
      // The interceptor's shape, `err.data.detail`; `err.response` never exists here.
      setError(errorDetail(err, t("akili could not apply this migration.")));
    } finally {
      if (!cancelled.current) setBusy(false);
    }
  };

  // Fires once, when the job reaches "applied", so the caller can refresh.
  const notified = useRef<string | null>(null);
  useEffect(() => {
    if (job?.status === "applied" && notified.current !== job.guid) {
      notified.current = job.guid;
      onApplied?.(job);
    }
  }, [job, onApplied]);

  const panelRef = useRef<HTMLDivElement | null>(null);
  /** What had focus when the modal opened; focus returns there on close. */
  const openerRef = useRef<HTMLElement | null>(null);

  /**
   * Move focus into the dialog on open and hand it back on close (WCAG 2.4.3; QA Swarm
   * finding on AKL-747 — every Tab stop landed in the form behind the overlay).
   *
   * The panel itself takes focus rather than its first control: it carries the dialog's
   * `aria-label`, so assistive technology announces what opened, and the first Tab then
   * lands on the × button. Reading `document.activeElement` here is reliable for this
   * modal, unlike `ReportProblemModal`: its callers mount it only while it is open, and the
   * button that opened it stays in the document, so the element captured is the opener.
   * Depends on the open transition alone so a re-render never yanks focus back.
   */
  useEffect(() => {
    if (!isOpen) return;
    openerRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panelRef.current?.focus();
    return () => {
      openerRef.current?.focus();
    };
  }, [isOpen]);

  /**
   * Escape closes the modal, except while a request is in flight (the × button is disabled
   * in that state for the same reason: the request is a write), and except when it comes
   * from a picker (a combobox) whose menu is open: react-select closes its own menu on that Escape but
   * does not stop it bubbling, so without this check one keypress would close the menu and
   * the whole modal (AKL-977). `aria-expanded` still reads "true" here because react-select
   * has only scheduled the close. Tab and Shift+Tab wrap at
   * the ends of the focusable controls so focus never reaches the page behind the overlay;
   * Shift+Tab from the panel itself, right after opening, goes to the last control.
   * Same shape as `ReportProblemModal.handlePanelKeyDown`.
   */
  const handlePanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      const target = event.target as HTMLElement;
      if (
        target.getAttribute("role") === "combobox" &&
        target.getAttribute("aria-expanded") === "true"
      )
        return;
      if (busy) return;
      onClose();
      return;
    }
    if (event.key !== "Tab") return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(
      panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex >= 0);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panel)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen) return null;

  const working =
    job !== null && isInFlight(job.status) && pollFailure === null;
  const plan = job?.plan ?? null;
  const result = job?.result ?? null;
  const canApply =
    job?.status === "planned" && (plan?.blocking.length ?? 0) === 0 && !busy;
  const canUpload =
    bundle !== null &&
    !busy &&
    selectedOrgId !== null &&
    (!createProject || timezone.trim() !== "");

  return (
    <div className={`${tailwindClassnames.modalOverlay} z-50`}>
      <div
        ref={panelRef}
        className={`${tailwindClassnames.modalPanel} max-w-2xl`}
        role="dialog"
        aria-modal="true"
        aria-label={t("Migrate from Wildlife Insights")}
        tabIndex={-1}
        onKeyDown={handlePanelKeyDown}
      >
        <div className={tailwindClassnames.modalHeaderRow}>
          <h2 className={tailwindClassnames.modalTitle}>
            {t("Migrate from Wildlife Insights")}
          </h2>
          <button
            type="button"
            className={tailwindClassnames.modalCloseButton}
            onClick={onClose}
            aria-label={t("Close")}
            disabled={busy}
          >
            &times;
          </button>
        </div>

        <div className={`${tailwindClassnames.modalBody} space-y-4`}>
          {job === null && (
            <>
              <p className={tailwindClassnames.helperText}>
                {t(
                  "Upload your Wildlife Insights download — the .zip containing projects.csv, cameras.csv, deployments.csv and images.csv. akili will show you what it can bring across before anything is saved.",
                )}
              </p>
              <p className={tailwindClassnames.helperText}>
                {t(
                  "Your images do not come from Wildlife Insights. Upload them with Akili Transfer, then run this bundle again to attach the identifications.",
                )}
              </p>

              {projectName && !createProject && (
                <p className={tailwindClassnames.basicLabel}>
                  {t("Migrating into {{project}}", { project: projectName })}
                </p>
              )}

              {createProject && (
                <p className={tailwindClassnames.helperText}>
                  {t(
                    "A Wildlife Insights download can contain several projects. akili will create one project for each, and tell you before it does.",
                  )}
                </p>
              )}

              <div>
                <label
                  className={tailwindClassnames.textInputLabel}
                  htmlFor="wi-bundle"
                >
                  {t("Wildlife Insights export")}
                </label>
                <input
                  id="wi-bundle"
                  type="file"
                  accept=".zip,.csv"
                  className={tailwindClassnames.detailsPageTextInput}
                  onChange={(event) =>
                    setBundle(event.target.files?.[0] ?? null)
                  }
                />
              </div>

              {/* Only when the caller gave a choice. With one organisation it is
                  preselected, because picking from a list of one is not a decision. */}
              {createProject && (organizations?.length ?? 0) > 1 && (
                <div>
                  <label
                    className={tailwindClassnames.textInputLabel}
                    htmlFor="wi-organisation"
                  >
                    {t("Organisation") + "*"}
                  </label>
                  <SelectInput
                    options={(organizations ?? []).map((org) => ({
                      value: org.org_id,
                      label: org.org_name ?? String(org.org_id),
                    }))}
                    className="w-full"
                    isDisabled={busy}
                    isLoading={false}
                    isClearable={false}
                    isSearchable={true}
                    isMulti={false}
                    name={t("Organisation")}
                    errors={undefined}
                    searchBar={false}
                    value={
                      selectedOrgId === null
                        ? null
                        : {
                            value: selectedOrgId,
                            label:
                              (organizations ?? []).find(
                                (org) => org.org_id === selectedOrgId,
                              )?.org_name ?? String(selectedOrgId),
                          }
                    }
                    onChange={(option: { value: number } | null) =>
                      setSelectedOrgId(option ? option.value : null)
                    }
                  />
                  <p className={tailwindClassnames.helperText}>
                    {t("The projects in this bundle will be created here.")}
                  </p>
                </div>
              )}

              {createProject && (
                <div>
                  {/* The same picker the New Project form uses, so only a zone the API
                      accepts can be chosen. It replaced a free-text box whose placeholder
                      read as a chosen zone while the field was empty (AKL-977). The
                      "required" hint doubles as the reason "Check this bundle" is
                      disabled (AKL-932). */}
                  <SelectInput
                    inputId="wi-timezone"
                    options={timezones.map((zone) => ({
                      value: zone,
                      label: zone,
                    }))}
                    className="w-full"
                    value={
                      timezone ? { value: timezone, label: timezone } : null
                    }
                    isDisabled={busy}
                    isLoading={timezonesLoading}
                    isClearable={true}
                    isSearchable={true}
                    isMulti={false}
                    required={true}
                    name={t("Time zone") + "*"}
                    errors={
                      timezonesError
                        ? t("Failed to load project timezones")
                        : timezone.trim()
                          ? undefined
                          : t("Choose a time zone to check this bundle.")
                    }
                    searchBar={false}
                    onChange={(option: { value: string } | null) =>
                      setTimezone(option ? option.value : "")
                    }
                  />
                  {timezonesError && (
                    <button
                      type="button"
                      className={tailwindClassnames.btnSecondary}
                      onClick={() => setTimezonesAttempt((n) => n + 1)}
                      disabled={busy || timezonesLoading}
                    >
                      {t("Try again")}
                    </button>
                  )}
                  {/* Not optional and not inferable: Wildlife Insights does not publish a
                      time zone, and akili needs one to date camera timestamps correctly
                      (AKL-722). Guessing would silently misdate every image. */}
                  <p className={tailwindClassnames.helperText}>
                    {t(
                      "Wildlife Insights does not record a time zone, so akili needs the one your cameras were set to.",
                    )}
                  </p>
                </div>
              )}

              <fieldset>
                <legend className={tailwindClassnames.basicSubHeading}>
                  {t("Options")}
                </legend>
                <div className="mt-1.5 space-y-2">
                  <div className="flex items-start gap-2">
                    <input
                      id="wi-create-deployments"
                      type="checkbox"
                      className={tailwindClassnames.checkBox}
                      checked={createMissingDeployments}
                      onChange={(event) =>
                        setCreateMissingDeployments(event.target.checked)
                      }
                    />
                    <label
                      htmlFor="wi-create-deployments"
                      className={tailwindClassnames.checkBoxLabel}
                    >
                      {t("Create deployments that are not in akili yet")}
                    </label>
                  </div>
                  <div className="flex items-start gap-2">
                    <input
                      id="wi-mark-reviewed"
                      type="checkbox"
                      className={tailwindClassnames.checkBox}
                      checked={markReviewed}
                      onChange={(event) =>
                        setMarkReviewed(event.target.checked)
                      }
                    />
                    <label
                      htmlFor="wi-mark-reviewed"
                      className={tailwindClassnames.checkBoxLabel}
                    >
                      {t(
                        "Mark catalogued images (reviewed by a person) as Reviewed in akili",
                      )}
                    </label>
                  </div>
                  {/* AKL-754 round 2. Off leaves the images nobody identified in Wildlife
                      Insights with no suggestion; the report then counts the guesses under
                      "Identified by Wildlife Insights AI, not reviewed". */}
                  <div className="flex items-start gap-2">
                    <input
                      id="wi-migrate-ai-predictions"
                      type="checkbox"
                      className={tailwindClassnames.checkBox}
                      checked={migrateAiPredictions}
                      onChange={(event) =>
                        setMigrateAiPredictions(event.target.checked)
                      }
                    />
                    <label
                      htmlFor="wi-migrate-ai-predictions"
                      className={tailwindClassnames.checkBoxLabel}
                    >
                      {t(
                        "Bring Wildlife Insights' AI predictions across as suggestions in akili",
                      )}
                    </label>
                  </div>
                </div>
              </fieldset>
            </>
          )}

          {working && (
            <div className="flex items-center gap-3 py-2">
              <BeatLoader size={8} color="#38bdf8" />
              <span className={tailwindClassnames.helperText}>
                {job?.status === "applying"
                  ? t("Migrating your data…")
                  : t("Reading your bundle…")}
              </span>
            </div>
          )}

          {pollFailure && (
            <p className={tailwindClassnames.warningLabel}>{pollFailure}</p>
          )}

          {job?.status === "failed" && (
            <div role="alert">
              <p className={tailwindClassnames.warningHeader}>
                {t("This migration failed")}
              </p>
              <p className={tailwindClassnames.helperText}>{job.error}</p>
            </div>
          )}

          {job?.status === "planned" && plan && (
            <>
              <p className={tailwindClassnames.helperText}>
                {t(
                  "Nothing has been saved yet. Review this and confirm below.",
                )}
              </p>
              <MigrationReport report={plan} />
            </>
          )}

          {job?.status === "applied" && result && (
            <>
              <p className={tailwindClassnames.basicLabel}>
                {t("Migration complete.")}
              </p>
              <MigrationReport report={result} applied />
            </>
          )}

          {error && (
            <p role="alert" className={tailwindClassnames.warningLabel}>
              {error}
            </p>
          )}
        </div>

        <div className={tailwindClassnames.modalFooter}>
          <button
            type="button"
            className={tailwindClassnames.btnSecondary}
            onClick={onClose}
            disabled={busy}
          >
            {job?.status === "applied" ? t("Done") : t("Cancel")}
          </button>

          {job === null && (
            <button
              type="button"
              className={
                canUpload
                  ? tailwindClassnames.btnPrimary
                  : tailwindClassnames.disabledSaveButton
              }
              onClick={handleUpload}
              disabled={!canUpload}
            >
              {t("Check this bundle")}
            </button>
          )}

          {job?.status === "planned" && (
            <button
              type="button"
              className={
                canApply
                  ? tailwindClassnames.btnPrimary
                  : tailwindClassnames.disabledSaveButton
              }
              onClick={handleApply}
              disabled={!canApply}
            >
              {t("Migrate this data")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MigrateFromWildlifeInsights;
